import { CalendarIcon, Check, CircleDashed, Clock3, StickyNote, TreePalm } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import {
  ApprovalStatus,
  TimeOffRequest,
  TimeOffRequestDay,
} from '@/modules/time-off/types/time-off-request';
import { TimeOffStatus } from '@/modules/time-off/types/time-off-status';
import { TimeOffTimeCategory } from '@/modules/time-off/types/time-off-time-category';
import {
  formatCompactDateRange,
  formatDisplayDate,
  formatWeekdayWithShortDate,
} from '@/utils/date-formatters';

import { getTimeOffDayLabel } from '../../helpers/time-off-day-label.helper';
import { TimeOffStatusSwatch } from './time-off-status-swatch';

interface TimeOffDetailsCardProps {
  /** The full time-off request (from a worklogs endpoint's `timeOffRequests`). */
  request: TimeOffRequest;
  /** Days of this request to surface explicitly (pre-filtered to the view's range, weekends removed). */
  dayLines?: TimeOffRequestDay[];
  /** Heading for the per-day section (e.g. "This day" in day view). Hidden when omitted. */
  dayLinesLabel?: string;
}

/**
 * Shared per-time-off details card reused by the day-view card, the week-view
 * expanded panel, and the month "time offs this month" section. Renders the
 * request's general info (type, status, time-category, date range, requested /
 * approved timestamps, approvers, description) and an optional per-day
 * breakdown. Driven by the generic `TimeOffRequest` model so the planner can
 * reuse it for its time-off blocks.
 */
export function TimeOffDetailsCard({ request, dayLines, dayLinesLabel }: TimeOffDetailsCardProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'authenticated.timeOff']);
  const isApproved = request.status === TimeOffStatus.Approved;

  return (
    <div className="group/timeoff flex flex-col gap-3 rounded-md border bg-card px-4 py-3 shadow transition-colors hover:bg-muted/50">
      {/* Header: type swatch + name + status pill + approvers */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-time-off-border text-time-off-text',
            isApproved ? 'time-off-approved' : 'time-off-pending'
          )}
        >
          <TreePalm className="relative z-[1] h-4 w-4" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate text-[13px] font-semibold text-foreground">
            {request.type.displayName}
          </span>
          <StatusPill status={request.status} />
        </div>

        {request.approvers.length > 0 && <ApproversBadge approvers={request.approvers} />}
      </div>

      {/* General info rows */}
      <div className="flex flex-col gap-1.5">
        <InfoRow icon={CalendarIcon}>
          <span className="flex flex-wrap items-center gap-1.5">
            {formatCompactDateRange(request.startDate, request.endDate)}
            <TimeCategoryBadge timeCategory={request.timeCategory} />
          </span>
        </InfoRow>

        <InfoRow icon={Clock3}>
          {t('authenticated.timeTracking:timeOff.requestedAtLabel', {
            date: formatDisplayDate(request.requestedAt),
          })}
        </InfoRow>

        {request.approvedAt && (
          <InfoRow icon={Check}>
            {t('authenticated.timeTracking:timeOff.approvedAtLabel', {
              date: formatDisplayDate(request.approvedAt),
            })}
          </InfoRow>
        )}

        {request.description && (
          <div className="flex items-start gap-1.5 overflow-hidden">
            <IconCell icon={StickyNote} />
            <TruncatedTooltip
              content={request.description}
              maxLines={2}
              className="text-[12.5px] text-muted-foreground"
            />
          </div>
        )}
      </div>

      {/* Per-day section (e.g. the selected day in day view) */}
      {dayLines && dayLines.length > 0 && (
        <div className="flex flex-col gap-1 border-t pt-2">
          {dayLinesLabel && (
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              {dayLinesLabel}
            </span>
          )}
          {dayLines.map(day => (
            <div key={day.date} className="flex items-center justify-between gap-2 text-[12.5px]">
              <span className="truncate text-foreground">
                {formatWeekdayWithShortDate(day.date)}
              </span>
              <span className="shrink-0 font-semibold text-foreground">
                {getTimeOffDayLabel(day, t)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: TimeOffStatus }) {
  const { t } = useTranslation('authenticated.timeOff');
  return (
    <span className="flex items-center gap-1.5 text-[11.5px] leading-tight text-time-off-text-soft">
      <TimeOffStatusSwatch isApproved={status === TimeOffStatus.Approved} />
      {t(`status.${status.toLowerCase()}`)}
    </span>
  );
}

function TimeCategoryBadge({ timeCategory }: { timeCategory: TimeOffTimeCategory }) {
  const { t } = useTranslation('authenticated.timeOff');
  return (
    <Badge variant="secondary" className="h-5 px-2 text-[10.5px] font-medium">
      {t(`timeCategory.${timeCategory.toLowerCase()}`)}
    </Badge>
  );
}

function IconCell({ icon: Icon }: { icon: typeof CalendarIcon }) {
  return (
    <span className="flex items-center justify-center pt-0.5">
      <Icon size={14} className="shrink-0 text-muted-foreground" />
    </span>
  );
}

function InfoRow({ icon, children }: { icon: typeof CalendarIcon; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5">
      <IconCell icon={icon} />
      <span className="text-[12.5px] leading-4 text-foreground">{children}</span>
    </div>
  );
}

function ApproversBadge({ approvers }: { approvers: TimeOffRequest['approvers'] }) {
  const { t } = useTranslation('authenticated.timeTracking');
  const approvedCount = approvers.filter(a => a.status === ApprovalStatus.Approved).length;

  const tooltip = (
    <ul className="flex flex-col gap-2">
      {approvers.map(approver => (
        <li key={approver.user.id} className="flex items-center gap-2">
          <EntityAvatar
            image={approver.user.profileImageUrl}
            name={approver.user.name}
            size="sm"
            shape="circle"
          />
          <span className="flex-1 text-sm text-foreground">{approver.user.name}</span>
          {approver.status === ApprovalStatus.Approved ? (
            <Check size={16} className="shrink-0 text-emerald-500" />
          ) : (
            <CircleDashed
              size={16}
              className={cn(
                'shrink-0',
                approver.status === ApprovalStatus.Rejected
                  ? 'text-destructive'
                  : 'text-muted-foreground'
              )}
            />
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <SimpleTooltip content={tooltip} side="bottom" contentClassName="p-3" variant="light">
      <Badge
        variant="outline"
        className="shrink-0 cursor-default border-time-off-border bg-time-off-soft px-2.5 py-1 text-[12px] font-medium text-time-off-text"
      >
        {t('timeOff.approvedCount', { approved: approvedCount, total: approvers.length })}
      </Badge>
    </SimpleTooltip>
  );
}
